import React from "react";
import Section from "../Section/Section";
import Link from "../Link/Link";
import styles from "./PaymentTermsAndConditions.module.less";
import { defineMessages, FormattedMessage } from "react-intl";
import longMessage from "../../../../../../../macros/longMessage.macro";
const messages = defineMessages({
    onlinePaymentAgreement: {
        id: "paymentTerms.onlinePaymentAgreement.header",
        defaultMessage: "Online Payment Agreement (“Agreement”)"
    },
    usingOnlinePayments: {
        id: "paymentTerms.usingOnlinePayments.header",
        defaultMessage: "Using Online Payments."
    },
    unauthorizedTransfersErrorsOrQuestions: {
        id: "paymentTerms.unauthorizedTransfersErrorsOrQuestions.header",
        defaultMessage: "Unauthorized Transfers, Errors or Questions."
    },
    liabilityForTransfers: {
        id: "paymentTerms.liabilityForTransfers.header",
        defaultMessage: "Liability for Transfers."
    },
    terminationChangesInsufficientFunds: {
        id: "paymentTerms.terminationChangesInsufficientFunds.header",
        defaultMessage: "Termination, Changes, Insufficient Funds."
    }
});
const Strong = ({ children }) => (<strong className={styles.strong}>{children}</strong>);
const PaymentTermsAndConditions = () => (<div>
		<div className="vr vr_x4">
			<h2 className={styles.heading}>
				<FormattedMessage id="paymentTerms.header" defaultMessage="DCMS ServiceLink® Terms and Conditions"/>
			</h2>
		</div>

		<Section title={messages.onlinePaymentAgreement}>
			<FormattedMessage tagName="p" id="paymentTerms.onlinePaymentAgreement.1" defaultMessage={longMessage `
					By utilizing the DCMS ServiceLink® payment option (“Online Payments”) you authorize DCM Services
					(“DCMS”) to initiate a one-time electronic funds transfer (“Transfer”) from your checking or savings
					account (“Funding Account”), using the information you provide to DCMS for the purpose of making a
					payment on the date (“Payment Date”) and in the amount you specify.
				`}/>

			<FormattedMessage tagName="p" id="paymentTerms.onlinePaymentAgreement.2" defaultMessage={longMessage `
					In order to utilize Online Payments, you must read and agree to this Agreement. This Agreement is in
					addition to our Privacy Policy ({link1}) and Website Terms of Use ({link2}), which also apply to you.
					In its sole discretion, DCMS may amend or change this Agreement from time to time.
				`} values={{
    link1: <Link href="https://www.dcmservices.com/privacy-policy"/>,
    link2: <Link href="https://www.dcmservices.com/terms-of-use"/>
}}/>
		</Section>

		<Section title={messages.usingOnlinePayments}>
			<p>
				<Strong>
					<FormattedMessage id="paymentTerms.usingOnlinePayments.1.1" defaultMessage={longMessage `
							You may cancel a Transfer by using DCMS ServiceLink® as a registered user, by calling DCMS at
							1-877-326-8786, or by contacting the financial institution that holds your Funding Account,
							at least three business days before the scheduled Payment Date for the cancellation to be effective.
						`}/>
				</Strong>
				{" "}
				<FormattedMessage id="paymentTerms.usingOnlinePayments.1.2" defaultMessage={longMessage `
						DCMS is not responsible for Transfers that are processed before it has a reasonable opportunity to act
						on your revocation or cancellation request.
					`}/>
			</p>

			<p>
				<Strong>
					<FormattedMessage id="paymentTerms.usingOnlinePayments.2.1" defaultMessage={longMessage `
							You or the estate on whose behalf you are acting may have additional accounts handled by DCM
							Services that are not displayed in DCMS ServiceLink®.
						`}/>
				</Strong>
				{" "}
				<FormattedMessage id="paymentTerms.usingOnlinePayments.2.2" defaultMessage={longMessage `
						Please contact DCMS via phone at 877-326-8786 or via mail at 1550 American Blvd E., Suite 200, Bloomington,
						MN 55425, to discuss any account handled by DCMS that is not listed on DCMS ServiceLink®.
					`}/>
			</p>
		</Section>

		<Section title={messages.unauthorizedTransfersErrorsOrQuestions}>
			<p>
				<FormattedMessage id="paymentTerms.unauthorizedTransfersErrorsOrQuestions.1" defaultMessage={longMessage `
						In case of errors or questions about Transfers made via Online Payments or if you suspect a Transfer has
						been or may be made without your permission, contact DCMS immediately in writing at {address}, or by phone
						at {phone}. You should also contact the financial institution that holds your Funding Account.
					`} values={{
    address: (<Strong>
								<FormattedMessage id="paymentTerms.unauthorizedTransfersErrorsOrQuestions.1.address" defaultMessage="DCM Services, LLC, 1550 American Blvd E., Suite 200, Bloomington, MN 55425"/>
							</Strong>),
    phone: (<Strong>
								<FormattedMessage id="paymentTerms.unauthorizedTransfersErrorsOrQuestions.1.phone" defaultMessage="1-877-326-8786"/>
							</Strong>)
}}/>
				{" "}
				<Strong>
					<FormattedMessage id="paymentTerms.unauthorizedTransfersErrorsOrQuestions.2" defaultMessage={longMessage `
							You are solely responsible for monitoring your Funding Account to verify that Transfers have been
							processed and to identify any issues related to Transfers.
						`}/>
				</Strong>
			</p>
		</Section>

		<Section title={messages.liabilityForTransfers}>
			<p>
				<FormattedMessage id="paymentTerms.liabilityForTransfers.1" defaultMessage={longMessage `
						DCMS is not liable for any loss or damage you may incur if: (1) you do not have enough money in your
						Funding Account to make a Transfer; (2) circumstances beyond DCMS control, including but not limited to,
						natural disaster, flood, earthquake, fire, war, act of terrorism, or acts of third parties in violation
						of federal, state, or international law, render DCMS unable to effectuate the Transfer despite
						reasonable precautions under the circumstances; (3) you provide incorrect Funding Account information to
						authorize a Transfer; or (4) the financial institution that holds your Funding Account fails to
						effectuate a Transfer, causes errors during a Transfer, or provides DCMS erroneous information.
					`}/>
				{" "}
				<Strong>
					<FormattedMessage id="paymentTerms.liabilityForTransfers.2" defaultMessage={longMessage `
							DCMS IS NOT RESPONSIBLE OR LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL,
							EXEMPLARY, PUNITIVE, OR OTHER DAMAGE RELATED TO YOUR USE OF ONLINE PAYMENTS.
						`}/>
				</Strong>
			</p>
		</Section>

		<Section title={messages.terminationChangesInsufficientFunds}>
			<FormattedMessage id="paymentTerms.terminationChangesInsufficientFunds" defaultMessage={longMessage `
					DCMS is under no obligation to provide Online Payments and may in its sole discretion and at any time
					without notice, modify, revoke access to or no longer offer Online Payments. It is your responsibility to
					ensure that you have sufficient funds to cover each Transfer. DCMS also has the option in its sole
					discretion to terminate your access to Online Payments if any Transfer is returned or reversed due to
					non-sufficient or insufficient funds, cancellation, stop-payment or any similar designation.
				`}/>
		</Section>
	</div>);
export default PaymentTermsAndConditions;



// WEBPACK FOOTER //
// ./src/assets/scripts/components/containers/public/legal/PaymentTermsAndConditions/PaymentTermsAndConditions.tsx